'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { captureException } from '@sentry/nextjs';
import styles from './study-error-client.module.css';

/* The outage body. Deliberately NOT the 404 page: this article exists, we just
 * could not read it this second, and telling the reader it is missing would be
 * both wrong and unrecoverable — there is nothing to retry on a 404.
 *
 * Light ground, same as the study page it stands in for, so a reader who hits
 * it during an outage does not also think the site broke. It says "not right
 * now" rather than "not found", and offers the two things that can actually
 * help: retry, or go back to the listing.
 *
 * The source set 503 + Retry-After: 30 from getServerSideProps. Next 16 has no
 * API for an arbitrary status out of a Server Component, so the status is a
 * 500 from this boundary and generateMetadata marks the response noindex.
 *
 * `reset` is deliberately unused: the failure is an upstream fetch, and
 * re-rendering the same tree against the same dead endpoint would fail again
 * with no new attempt at the CMS. A full reload is the retry that means
 * something. */
export default function StudyDetailError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('science');
  void reset;

  // Same contract as app/error.tsx, and it has to be repeated here because Next
  // only runs the NEAREST error boundary — without this, a client render error
  // in this subtree is caught, shown as the outage screen, and reported to
  // nobody. See the digest reasoning in app/error.tsx: a digest means
  // onRequestError already captured it server-side, and the client's copy is
  // Next's redacted placeholder.
  useEffect(() => {
    if (!error.digest) {
      try {
        captureException(error);
      } catch {
        /* the reporter must never take down the recovery screen */
      }
    }
    console.error(error);
  }, [error]);
  return (
    <div className={styles.errorWrap}>
      <span className={styles.eyebrow}>{t('science.studyError.eyebrow')}</span>
      <h1 className={styles.title}>{t('science.studyError.title')}</h1>
      <p className={styles.body}>{t('science.studyError.body')}</p>
      <div className={styles.actions}>
        <button
          className={styles.button}
          type="button"
          onClick={() => window.location.reload()}
        >
          {t('science.studyError.retry')}
        </button>
        <Link className={styles.link} href="/science/studies/">
          {t('science.studyError.allStudies')}
        </Link>
      </div>
    </div>
  );
}
